/**
 * Navigation Configuration for Auxilio Travel
 * Defines sidebar and navigation menu items with collapsible categories
 */

import {
  MapIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  CalculatorIcon,
  CloudIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
  TruckIcon,
  HeartIcon,
  WrenchScrewdriverIcon,
  LanguageIcon,
  CakeIcon,
} from '@heroicons/react/24/outline';

export interface NavigationItem {
  id: string;
  labelKey: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  enabled: boolean;
  descriptionKey?: string;
  releaseQuarter?: string;
  badgeKey?: string;
}

export interface NavigationCategory {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavigationItem[];
  defaultExpanded?: boolean;
}

// Top-level navigation items (not inside a collapsible category)
export const topLevelNavigationItems: NavigationItem[] = [
  {
    id: 'destinations',
    labelKey: 'common:navigation.items.destinations',
    path: '/destinations',
    icon: MapIcon,
    enabled: true,
    descriptionKey: 'common:navigation.descriptions.destinations',
  },
  {
    id: 'itinerary',
    labelKey: 'common:navigation.items.itinerary',
    path: '/itinerary',
    icon: CalendarDaysIcon,
    enabled: true,
    descriptionKey: 'common:navigation.descriptions.itinerary',
  },
  {
    id: 'ask-questions',
    labelKey: 'common:navigation.items.askQuestions',
    path: '/ask-questions',
    icon: ChatBubbleLeftRightIcon,
    enabled: true,
    descriptionKey: 'common:navigation.descriptions.askQuestions',
  },
  {
    id: 'travel-glossary',
    labelKey: 'common:navigation.items.travelGlossary',
    path: '/travel-glossary',
    icon: BookOpenIcon,
    enabled: true,
    descriptionKey: 'common:navigation.descriptions.travelGlossary',
  },
  {
    id: 'calculators',
    labelKey: 'common:navigation.items.calculators',
    path: '/calculators',
    icon: CalculatorIcon,
    enabled: true,
    descriptionKey: 'common:navigation.descriptions.calculators',
  },
];

export const navigationCategories: NavigationCategory[] = [
  {
    id: 'travel-tools',
    labelKey: 'common:navigation.categories.travelTools',
    icon: WrenchScrewdriverIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'weather',
        labelKey: 'common:navigation.items.weather',
        path: '/weather',
        icon: CloudIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.weather',
      },
      {
        id: 'flights',
        labelKey: 'common:navigation.items.flights',
        path: '/flights',
        icon: PaperAirplaneIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.flights',
      },
      {
        id: 'accommodations',
        labelKey: 'common:navigation.items.accommodations',
        path: '/accommodations',
        icon: BuildingOfficeIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.accommodations',
      },
      {
        id: 'transport',
        labelKey: 'common:navigation.items.transport',
        path: '/transport',
        icon: TruckIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.transport',
      },
      {
        id: 'cultural',
        labelKey: 'common:navigation.items.cultural',
        path: '/cultural',
        icon: HeartIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.cultural',
      },
      {
        id: 'phrasebook',
        labelKey: 'common:navigation.items.phrasebook',
        path: '/phrasebook',
        icon: LanguageIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.phrasebook',
      },
      {
        id: 'cuisine',
        labelKey: 'common:navigation.items.cuisine',
        path: '/cuisine',
        icon: CakeIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.cuisine',
      },
    ],
  },
];

// Utility functions
export const getAllNavigationItems = (): NavigationItem[] => {
  return [...topLevelNavigationItems, ...navigationCategories.flatMap((category) => category.items)];
};

export const findNavigationItemByPath = (path: string): NavigationItem | undefined => {
  return getAllNavigationItems().find((item) => item.path === path);
};

export const getCategoryForPath = (path: string): NavigationCategory | undefined => {
  return navigationCategories.find((category) =>
    category.items.some((item) => item.path === path)
  );
};
